import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueBreakdown } from '../../../context';
import messages from './messages';

type Props = {
	onDiscard(): void;
	isModalOpen?: boolean;
};

export const DiscardSuggestions = ({ onDiscard }: Props) => {
	const [
		{ isDiscardSuggestionsModalOpen },
		{ openDiscardSuggestionsModal, closeDiscardSuggestionsModal },
	] = useIssueBreakdown();

	const { formatMessage } = useIntl();

	const onDiscardClick = useCallback(() => {
		onDiscard();
		closeDiscardSuggestionsModal();
	}, [closeDiscardSuggestionsModal, onDiscard]);

	return (
		<>
			<ButtonsRightSide>
				<StyledButton
					testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.discard-suggestions.discard-suggestions-button"
					type="button"
					appearance="subtle"
					onClick={openDiscardSuggestionsModal}
				>
					{formatMessage(messages.labelDiscardSuggestions)}
				</StyledButton>
			</ButtonsRightSide>
			<ModalTransition>
				{isDiscardSuggestionsModalOpen && (
					<Modal
						testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.discard-suggestions.modal"
						onClose={closeDiscardSuggestionsModal}
					>
						<ModalHeader>
							<ModalTitle appearance="warning" isMultiline>
								{formatMessage(messages.warningTitleModal)}
							</ModalTitle>
						</ModalHeader>
						<ModalBody testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.discard-suggestions.body">
							{formatMessage(messages.warningBodyModal)}
						</ModalBody>
						<ModalFooter>
							<Button
								appearance="subtle"
								aria-label={formatMessage(messages.labelCancelModal)}
								testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.discard-suggestions.cancel-button"
								onClick={closeDiscardSuggestionsModal}
							>
								{formatMessage(messages.labelCancelModal)}
							</Button>

							<Button
								appearance="warning"
								testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.discard-suggestions.discard-button"
								aria-label={formatMessage(messages.labelDiscardModal)}
								onClick={onDiscardClick}
							>
								{formatMessage(messages.labelDiscardModal)}
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsRightSide = styled.div({
	justifyContent: 'flex-end',
	display: 'flex',
	margin: `${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)({
	marginLeft: `${token('space.100', '8px')}`,
});
